import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { passengerCMSApi, post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useChildAirportDocumentDetails } from "./ChildAirport.hooks";

export const AddEditChildAirport = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const {
    data: editChildAirport,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = useChildAirportDocumentDetails(query.get("editId") || "");

  const {
    data: editChildAirportById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [airportOptions, setAirportOptions] = useState<any[]>([]);

  // Fetch dropdowns
  const fetchDropdowns = async () => {
    try {
      const [airportsRes] = await Promise.all([passengerCMSApi.get(`/api/airport-category/parent-term`)]);

      const validAirports =
        airportsRes.data
          ?.filter((item: any) => item.published === "true")
          .map((item: any) => ({
            label: decodeHTML(item.parent_term_name),
            value: item.parent_term_id,
          })) || [];

      setAirportOptions(validAirports);
    } catch (error) {
      console.error("Dropdown fetch failed", error);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      name: editChildAirportById
        ? decodeHTML(editChildAirportById.payload?.name)
        : editChildAirport
          ? decodeHTML(editChildAirport?.name)
          : "",
      title_malay: editChildAirportById
        ? decodeHTML(editChildAirportById.payload?.title_malay)
        : editChildAirport
          ? decodeHTML(editChildAirport?.title_malay)
          : "",
      title_mandarin: editChildAirportById
        ? decodeHTML(editChildAirportById.payload?.title_mandarin)
        : editChildAirport
          ? decodeHTML(editChildAirport?.title_mandarin)
          : "",

      parent_term_name:
        typeof editChildAirportById?.payload?.parent_term_name === "object"
          ? editChildAirportById?.payload?.parent_term_name
          : editChildAirportById?.payload?.parent_term_id
            ? {
                value: String(editChildAirportById?.payload?.parent_term_id),
                label: editChildAirportById?.payload?.parent_term_name || "",
              }
            : typeof editChildAirport?.parent_term_name === "object"
              ? editChildAirport?.parent_term_name
              : editChildAirport?.parent_term_id
                ? {
                    value: String(editChildAirport?.parent_term_id),
                    label: editChildAirport?.parent_term_name || "",
                  }
                : null,

      published: editChildAirportById
        ? decodeHTML(editChildAirportById.payload.published)
        : editChildAirport
          ? decodeHTML(editChildAirport?.published)
          : false,
    },
    // fields: fields.childAirport,
    fields: fields.childAirport.map((field: any) => {
      if (field.name === "parent_term_name")
        return {
          ...field,
          options: airportOptions,
        };
      return field;
    }),
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    console.log("data-123", data);

    let imageKeys: any = ["field_common_image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    // Normalize parent_term_name to { value, label }
    if (data.parent_term_name) {
      const selectedOption = airportOptions.find(
        (opt) => opt.value === data.parent_term_name || opt.value === data.parent_term_name.value
      );
      data.parent_term_name = selectedOption
        ? { value: String(selectedOption.value), label: selectedOption.label }
        : typeof data.parent_term_name === "string"
          ? { value: data.parent_term_name, label: data.parent_term_name }
          : data.parent_term_name;
    }

    const payload = {
      action,
      method,
      module: "Maps",
      subModule: "childAirport",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editChildAirport?.id || editChildAirportById?.payload?.id }),
        ...data,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Child Airport`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${passengerHost}/api/get-airport-category-crud-api-data/${editChildAirport?.id || editChildAirportById?.payload?.id}` : `${passengerHost}/api/create-airport-category-crud-api-data`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Maps"
      />
    </ContentWrapper>
  );
};
